import React, { useMemo, useState } from 'react'
import { Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useFormContext, useWatch } from 'react-hook-form'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { PositionedAction } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import StyledSwitch from '@e3dc-react/shell/Elements/Switch/StyledSwitch'
import { NumberField, Select } from 'src/Libs/inputs/react-hook-form'
import { FeedInTariffType } from 'src/Libs/dynamic-power-prices/types'
import { UnitTypes } from 'src/Libs/types'
import { InformationTile } from '../common'
import ConfirmSettingChangeDialog from './ConfirmSettingChangeDialog'
import { Ai360SettingsFormValues } from '../../types'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const useStyles = makeStyles()(theme => ({
  title: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
    },
  },
}))

const PVSettingSubPage: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const [shouldWarnAboutUnsavedChanges, setShouldWarnAboutUnsavedChanges] = useState(false)

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { isDirty, defaultValues },
  } = useFormContext<Ai360SettingsFormValues>()
  const isAI360Active = useWatch({ control, name: 'isAI360Active' })
  const isChargingFromPVActive = useWatch({ control, name: 'isChargingFromPVActive' })
  const pvSettings = useWatch({ control, name: 'pvSettings' })

  const handleToggle = (): void => {
    const newValue = !isChargingFromPVActive
    setValue('isChargingFromPVActive', newValue, { shouldDirty: true })
    if (newValue === false) {
      // reset feed-in tariff type to default value when deactivating charging from PV to prevent invalid configuration
      setValue('pvSettings.feedInTariffType', '', { shouldDirty: true })
    }
    if (!isAI360Active && newValue) {
      setValue('isAI360Active', true, { shouldDirty: true })
    }
  }

  const validateOnUnsavedChanges = (): void => {
    if (isDirty) {
      setShouldWarnAboutUnsavedChanges(true)
    } else {
      onClose()
    }
  }

  const handleConfirmUnsavedChanges = (): void => {
    const { isChargingFromPVActive, pvSettings, ...rest } = getValues()
    reset({ ...rest, pvSettings: defaultValues?.pvSettings, isChargingFromPVActive: defaultValues?.isChargingFromPVActive }, { keepDirty: true })
    setShouldWarnAboutUnsavedChanges(false)
    onClose()
  }

  const disableSaveAction = useMemo(() => {
    if (!isDirty) return true
    // PV settings is turned off.
    if (!isChargingFromPVActive) return true
    // PV settings is turned on, but feedInTariffType or feedInTariff is not set.
    if (isChargingFromPVActive && (pvSettings.feedInTariffType === '' || pvSettings.feedInTariff === '')) return true
  }, [isChargingFromPVActive, isDirty, pvSettings.feedInTariff, pvSettings.feedInTariffType])

  const secondaryAction: PositionedAction = {
    position: '3',
    style: 'secondary',
    name: t('common.cancel'),
    onClick: validateOnUnsavedChanges,
  }

  const primaryAction: PositionedAction = {
    position: '4',
    style: 'primary',
    name: t('common.next'),
    onClick: onClose,
    disabled: disableSaveAction,
  }

  return (
    <>
      <Dialog
        fullScreen={!upMd}
        open={isOpen}
        navigateBackButton={true}
        closeButtonVisible={false}
        onClose={validateOnUnsavedChanges}
        onNavigateBack={validateOnUnsavedChanges}
        title={t('ai360.settings-page.charging-from-pv.title')}
        dialogTitleClassName={classes.title}
        actions={[primaryAction, secondaryAction]}
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2 }}>
          <Typography variant="h3">{t('ai360.settings-page.charging-from-pv.title')}</Typography>
          <StyledSwitch checked={isChargingFromPVActive} onChange={handleToggle} />
        </Box>

        <Stack sx={{ pt: 2 }} gap={4}>
          <Typography variant="body2">{t('ai360.settings-page.pv-settings.description')}</Typography>

          {isChargingFromPVActive && <InformationTile message={t('ai360.settings-page.pv-settings.confirm-pv-activation.info')} />}

          <Stack gap={2}>
            <Typography variant="h3">{t('ai360.settings-page.pv-settings.feed-in-tariff-type')}</Typography>
            <Select
              name="pvSettings.feedInTariffType"
              options={Object.values(FeedInTariffType).map(value => ({
                label: t(`ai360.settings-page.pv-settings.feed-in-tariff-type.${value}`),
                value,
              }))}
            />
            {pvSettings.feedInTariffType && (
              <InformationTile
                message={
                  pvSettings.feedInTariffType === FeedInTariffType.Fixed
                    ? t('ai360.settings-page.pv-settings.feed-in-tariff-type.fixed-type-description')
                    : t('ai360.settings-page.pv-settings.feed-in-tariff-type.zero-type-description')
                }
              />
            )}
          </Stack>

          <Stack gap={2}>
            <Typography variant="h3">{t('ai360.settings-page.pv-settings.feed-in-tariff')}</Typography>
            <NumberField name="pvSettings.feedInTariff" suffix={UnitTypes.centsPerKiloWattHour} disabled={pvSettings.feedInTariffType === ''} />
          </Stack>

          {pvSettings.feedInTariffType && pvSettings.feedInTariffType === FeedInTariffType.Fixed && (
            <Stack gap={2} mt={3}>
              <Typography variant="h3">{t('ai360.settings-page.pv-settings.power-reduction-from')}</Typography>
              <NumberField name="pvSettings.powerReductionFrom" suffix={UnitTypes.percentage} disabled />
              <InformationTile message={t('ai360.settings-page.pv-settings.power-reduction-from-description')} />
            </Stack>
          )}
        </Stack>
      </Dialog>
      {shouldWarnAboutUnsavedChanges && (
        <ConfirmSettingChangeDialog
          isOpen={shouldWarnAboutUnsavedChanges}
          onClose={() => setShouldWarnAboutUnsavedChanges(false)}
          onConfirm={handleConfirmUnsavedChanges}
          title={t('ai360.settings-page.pv-settings.confirm-unsaved-changes.title')}
          description={t('ai360.settings-page.pv-settings.confirm-unsaved-changes.description')}
          primaryActionText={t('common.promptDialog.confirmButtonText')}
        />
      )}
    </>
  )
}

export default PVSettingSubPage
